import { useCallback, useMemo, useState } from "react";
import { Avatar } from "../components/Avatar";
import { GrownUps } from "../components/GrownUps";
import {
  api,
  ApiError,
  type CashOutRequest,
  type Child,
  type Me,
  type PeriodDecision,
  type ReviewQueue,
  type TransferRequest,
} from "../lib/api";
import { formatCents } from "../lib/money";
import { useLiveData } from "../lib/useLiveData";
import { Shell } from "./KidHome";
import { Field } from "./Setup";

/* The grown-up's screen -- the other side of the child's passbook.
 *
 * The parent is the bank, so this is the counter: everything waiting is a slip
 * on ONE sheet, and the children are accounts on the shelf below. The previous
 * version gave each queue its own card, which made "your password" as loud as
 * "cash to hand over" and moved the page around depending on what happened to
 * be pending. Now the page has a fixed shape and only the counter changes
 * height.
 *
 * Identity sits in the utility rule at the bottom rather than the headline: a
 * parent knows their own name, and the one question this screen exists to
 * answer is what needs them.
 */
export function ParentHome({
  me,
  onSignOut,
  onOpenChild,
  onAddChild,
  onManageChores,
}: {
  me: Me;
  onSignOut: () => void;
  onOpenChild: (id: string) => void;
  onAddChild: () => void;
  onManageChores: () => void;
}) {
  const [children, setChildren] = useState<Child[]>([]);
  const [queue, setQueue] = useState<ReviewQueue | null>(null);
  const [cashOuts, setCashOuts] = useState<CashOutRequest[]>([]);
  const [moves, setMoves] = useState<TransferRequest[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [panel, setPanel] = useState<"password" | "grown-ups" | "email" | null>(null);

  const load = useCallback(async () => {
    try {
      const [kids, q, co, mv] = await Promise.all([
        api.get<Child[]>("/children"),
        api.get<ReviewQueue>("/review-queue"),
        api.get<{ requests: CashOutRequest[] }>("/cash-out"),
        api.get<{ requests: TransferRequest[] }>("/transfer-requests"),
      ]);
      setChildren(kids);
      setQueue(q);
      setCashOuts(co.requests);
      setMoves(mv.requests);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load.");
    }
  }, []);

  useLiveData(load);

  async function act(id: string, fn: () => Promise<unknown>) {
    setBusy(id);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "That didn't work.");
    } finally {
      setBusy(null);
    }
  }

  /* Every slip carries the colour of the child it belongs to. Four slips from
     three kids are otherwise indistinguishable at arm's length. */
  const hue = useMemo(() => {
    const byId = new Map(children.map((c) => [c.id, c.color]));
    return (childId: string) => byId.get(childId) || "var(--kraft-deep)";
  }, [children]);

  const waiting = (queue?.total ?? 0) + cashOuts.length + moves.length;

  const groups: { key: string; title: string; hint?: string; body: React.ReactNode }[] = [];

  // Cash first: it is the only one where real coins leave a real hand.
  if (cashOuts.length > 0) {
    groups.push({
      key: "cash",
      title: "Cash to hand over",
      hint: "Mark it paid once the money is actually in their hand.",
      body: cashOuts.map((r) => (
        <Slip key={r.id} hue={hue(r.childId)}>
          <SlipHead
            who={r.childName}
            what={`from ${r.bucketName}`}
            note={r.note}
            amount={r.amountCents}
          />
          <div className="slip-do">
            <button
              disabled={busy === r.id}
              onClick={() => act(r.id, () => api.post(`/cash-out/${r.id}/pay`))}
              style={{ background: "var(--save-soft)" }}
            >
              Mark it paid
            </button>
            <button
              disabled={busy === r.id}
              onClick={() => act(r.id, () => api.post(`/cash-out/${r.id}/deny`))}
            >
              Turn it down
            </button>
          </div>
        </Slip>
      )),
    });
  }

  if (moves.length > 0) {
    groups.push({
      key: "moves",
      title: "Money to move",
      hint: "Their total is the same either way you answer.",
      body: moves.map((m) => (
        <Slip key={m.id} hue={hue(m.childId)}>
          <SlipHead
            who={m.childName}
            what={`${m.fromName} → ${m.toName}`}
            note={m.note}
            amount={m.amountCents}
          />
          <div className="slip-do">
            <button
              disabled={busy === m.id}
              onClick={() => act(m.id, () => api.post(`/transfer-requests/${m.id}/approve`))}
              style={{ background: "var(--save-soft)" }}
            >
              Move it
            </button>
            <button
              disabled={busy === m.id}
              onClick={() => act(m.id, () => api.post(`/transfer-requests/${m.id}/deny`))}
            >
              Not yet
            </button>
          </div>
        </Slip>
      )),
    });
  }

  if (queue && queue.chores.length > 0) {
    // Five rows all reading "Fri, Aug 28" is five copies of one fact. When the
    // queue is a single day -- which is the ordinary case -- the day is said
    // once, in the caption, and the rows carry only what differs.
    const days = new Set(queue.chores.map((c) => c.dueLocalDate));
    const oneDay = days.size === 1 ? shortDay(queue.chores[0].dueLocalDate) : null;
    groups.push({
      key: "chores",
      title: "Jobs to check",
      hint: oneDay ?? undefined,
      body: queue.chores.map((c) => (
        <Slip key={c.id} hue={hue(c.childId)}>
          <SlipHead
            who={c.childName}
            what={c.name}
            meta={oneDay ? undefined : shortDay(c.dueLocalDate)}
          />
          <div className="slip-do">
            <button
              disabled={busy === c.id}
              onClick={() =>
                act(c.id, () =>
                  api.post(`/chore-instances/${c.id}/review`, { approve: true }),
                )
              }
              style={{ background: "var(--save-soft)" }}
            >
              Done
            </button>
            <button
              disabled={busy === c.id}
              onClick={() =>
                act(c.id, () =>
                  api.post(`/chore-instances/${c.id}/review`, { approve: false }),
                )
              }
            >
              Not yet
            </button>
            <button
              disabled={busy === c.id}
              title="Doesn't count for or against them"
              onClick={() =>
                act(c.id, () =>
                  api.post(`/chore-instances/${c.id}/excuse`, { approve: true }),
                )
              }
            >
              Excuse
            </button>
          </div>
        </Slip>
      )),
    });
  }

  /* Two different questions, asked separately.
   *
   * A week with no records ("we were away, the NAS was off") and a week whose
   * chart was kept and came up short are the same row in the database and
   * nothing alike to a parent. The first has no figures to reason about and
   * only needs an amount; the second has real figures, and the answer is
   * usually not about money at all -- it is "they did do it, nobody ticked
   * it off". Mixing them into one list gets one of them answered wrongly. */
  const shortWeeks = queue?.periods.filter((p) => p.reviewReason === "chores_incomplete") ?? [];
  const blankWeeks = queue?.periods.filter((p) => p.reviewReason !== "chores_incomplete") ?? [];

  if (shortWeeks.length > 0) {
    groups.push({
      key: "short",
      title: "Jobs not finished",
      hint: "These didn't reach the jobs they needed, so nothing has been paid yet. Nothing moves until you say.",
      body: shortWeeks.map((p) => (
        <ShortWeek
          key={p.id}
          period={p}
          hue={hue(p.childId)}
          busy={busy === p.id}
          onDecide={(decision, amountCents) =>
            act(p.id, () =>
              api.post(`/periods/${p.id}/decide`, { decision, amountCents }),
            )
          }
        />
      )),
    });
  }

  if (blankWeeks.length > 0) {
    groups.push({
      key: "periods",
      title: "Missed paydays",
      hint: "Nobody was checking off jobs during these, so there is nothing to work the amount out from. Set what each one should pay.",
      body: blankWeeks.map((p) => (
        <Slip key={p.id} hue={hue(p.childId)}>
          <SlipHead who={p.childName} meta={p.periodLabel} amount={p.baseAmountCents} />
          <div className="slip-do">
            <button
              disabled={busy === p.id}
              onClick={() =>
                act(p.id, () =>
                  api.post(`/periods/${p.id}/approve`, { amountCents: p.baseAmountCents }),
                )
              }
              style={{ background: "var(--save-soft)" }}
            >
              Pay it
            </button>
            <button
              disabled={busy === p.id}
              onClick={() => act(p.id, () => api.post(`/periods/${p.id}/skip`))}
            >
              Skip
            </button>
          </div>
        </Slip>
      )),
    });
  }

  return (
    <Shell>
      <header>
        {waiting > 0 ? (
          <>
            <Tally count={waiting} />
            <h1 style={{ fontSize: 27, marginTop: 12 }}>
              {waiting === 1 ? "One thing needs you" : `${waiting} things need you`}
            </h1>
          </>
        ) : (
          <>
            <h1 style={{ fontSize: 27 }}>Nothing needs you right now</h1>
            <p style={{ margin: "6px 0 0", color: "var(--ink-2)" }}>
              Checked-off jobs, cash requests and jar moves land here.
            </p>
          </>
        )}
      </header>

      {groups.length > 0 && (
        <>
          <p className="eyebrow">The counter</p>
          <section className="card">
            {groups.map((g, i) => (
              <div
                key={g.key}
                style={
                  i === 0
                    ? undefined
                    : {
                        marginTop: 20,
                        paddingTop: 18,
                        borderTop: "1px solid var(--paper-edge)",
                      }
                }
              >
                <p className="cap">{g.title}</p>
                {g.hint && <p className="cap-hint">{g.hint}</p>}
                <div style={{ display: "grid", gap: 10, marginTop: 12 }}>{g.body}</div>
              </div>
            ))}
          </section>
        </>
      )}

      <p className="eyebrow">Accounts</p>
      <section className="card">
        {children.length === 0 ? (
          <div style={{ display: "grid", gap: 12, justifyItems: "start" }}>
            <p style={{ color: "var(--ink-2)", margin: 0 }}>
              No kids yet. Add one and this becomes their bank.
            </p>
            <button onClick={onAddChild}>Add a kid</button>
          </div>
        ) : (
          <>
            <div style={{ display: "grid", gap: 10 }}>
              {children.map((c) => (
                <button
                  key={c.id}
                  onClick={() => onOpenChild(c.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "12px 14px",
                    textAlign: "left",
                    border: "2px solid var(--paper-edge)",
                    borderLeft: `7px solid ${c.color || "var(--kraft-deep)"}`,
                    boxShadow: "none",
                    minHeight: 0,
                  }}
                >
                  <Avatar name={c.avatar} size={44} ring={c.color} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span
                      style={{
                        display: "block",
                        fontFamily: "var(--font-display)",
                        fontSize: 17,
                      }}
                    >
                      {c.displayName}
                    </span>
                    <span style={{ fontSize: 12.5, color: "var(--ink-3)" }}>
                      Set up for {c.presetLabel}
                    </span>
                  </span>
                  <span style={{ textAlign: "right" }}>
                    <span
                      className="money"
                      style={{ display: "block", fontSize: 18, fontWeight: 700 }}
                    >
                      {formatCents(c.balances.bankCents)}
                    </span>
                    {c.balances.walletCents > 0 && (
                      <span className="money" style={{ fontSize: 12.5, color: "var(--ink-3)" }}>
                        👛 {formatCents(c.balances.walletCents)}
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </div>
            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                marginTop: 16,
                paddingTop: 14,
                borderTop: "1px solid var(--paper-edge)",
              }}
            >
              <button onClick={onAddChild} style={{ flex: "1 1 140px", minHeight: 48, fontSize: 15 }}>
                Add a kid
              </button>
              <button
                onClick={onManageChores}
                style={{ flex: "1 1 140px", minHeight: 48, fontSize: 15 }}
              >
                Set up jobs
              </button>
            </div>
          </>
        )}
      </section>

      {error && (
        <p role="alert" style={{ color: "var(--bad)", fontWeight: 600, marginTop: 14 }}>
          {error}
        </p>
      )}

      {panel === "grown-ups" && <GrownUps onCancel={() => setPanel(null)} />}

      {panel === "password" && (
        <section className="card" style={{ marginTop: 20 }}>
          <h2 style={{ fontSize: 18, marginBottom: 4 }}>Change your password</h2>
          <p style={{ margin: "0 0 14px", fontSize: 13.5, color: "var(--ink-2)" }}>
            This signs you out everywhere except here.
          </p>
          <ChangePassword
            onCancel={() => setPanel(null)}
            onDone={() => setPanel(null)}
          />
        </section>
      )}

      {panel === "email" && (
        <section className="card" style={{ marginTop: 20 }}>
          <h2 style={{ fontSize: 18, marginBottom: 4 }}>Recovery email</h2>
          <p style={{ margin: "0 0 14px", fontSize: 13.5, color: "var(--ink-2)" }}>
            Used only to send you a link if you ever forget your password.
          </p>
          <EmailSettings
            initialEmail={me.email}
            onCancel={() => setPanel(null)}
            onDone={() => setPanel(null)}
          />
        </section>
      )}

      <footer className="utility">
        <span style={{ flex: 1, minWidth: 140 }}>Signed in as {me.displayName}</span>
        {/* One flex item, not two, so the pair drops to the next line together
            at phone width instead of stranding "Sign out" on its own. */}
        <span className="utility-do">
          {panel !== "grown-ups" && (
            <button className="quiet-link" onClick={() => setPanel("grown-ups")}>
              Grown-ups
            </button>
          )}
          {panel !== "email" && (
            <button className="quiet-link" onClick={() => setPanel("email")}>
              Recovery email
            </button>
          )}
          {panel !== "password" && (
            <button className="quiet-link" onClick={() => setPanel("password")}>
              Change password
            </button>
          )}
          <button className="quiet-link" onClick={onSignOut}>
            Sign out
          </button>
        </span>
      </footer>
    </Shell>
  );
}

/* A week that fell short of its jobs.
 *
 * Three answers, because the chart cannot tell them apart and a parent can.
 * They are ordered by how often they are true rather than by severity: the
 * commonest reason a week comes up short in a real house is that the jobs were
 * done on a Tuesday nobody was holding the tablet, so "They were done" is
 * first and it is the one that fixes the CHART as well as the money. Paying in
 * full while the week is still drawn as a row of misses would teach a child
 * the squares mean nothing.
 *
 * The part-payment amount hides behind its own button. Shown open, a text
 * field is the loudest thing on the slip and reads as the expected answer;
 * it is in fact the rarest of the three.
 */
function ShortWeek({
  period,
  hue,
  busy,
  onDecide,
}: {
  period: ReviewQueue["periods"][number];
  hue: string;
  busy: boolean;
  onDecide: (decision: PeriodDecision, amountCents?: number) => void;
}) {
  const [partial, setPartial] = useState(false);
  // Seeded with what the rules worked out -- usually $0.00 under a threshold.
  // It is a starting point to type over, and it is also the honest answer to
  // "what would have happened if nobody looked".
  const [amount, setAmount] = useState(
    ((period.computedAmountCents ?? 0) / 100).toFixed(2),
  );

  const cents = Math.round(parseFloat(amount) * 100);
  const valid = Number.isFinite(cents) && cents >= 0 && cents <= period.baseAmountCents;
  const jobs =
    period.choresRequired != null && period.choresDone != null
      ? `${period.choresDone} of ${period.choresRequired} jobs`
      : undefined;

  return (
    <Slip hue={hue}>
      <SlipHead
        who={period.childName}
        what={jobs}
        meta={period.periodLabel}
        amount={period.baseAmountCents}
      />
      {partial ? (
        <div className="slip-do">
          <input
            inputMode="decimal"
            autoFocus
            aria-label={`How much to pay for ${period.periodLabel}`}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="money"
            style={{ width: 92, minHeight: 42, textAlign: "right" }}
          />
          <button
            disabled={busy || !valid}
            onClick={() => onDecide("partial", cents)}
            style={{ background: "var(--save-soft)" }}
          >
            Pay this
          </button>
          <button disabled={busy} onClick={() => setPartial(false)}>
            Cancel
          </button>
        </div>
      ) : (
        <div className="slip-do">
          <button
            disabled={busy}
            title="Ticks off the jobs for that week too, so the chart matches"
            onClick={() => onDecide("full")}
            style={{ background: "var(--save-soft)" }}
          >
            They were done
          </button>
          <button disabled={busy} onClick={() => setPartial(true)}>
            Pay some
          </button>
          <button
            disabled={busy}
            title="Goes on their statement as a $0.00 payday, with the jobs it counted"
            onClick={() => onDecide("none")}
          >
            Not earned
          </button>
        </div>
      )}
      {partial && !valid && (
        <p style={{ flexBasis: "100%", margin: 0, fontSize: 13, color: "var(--bad)" }}>
          Give an amount between {formatCents(0)} and {formatCents(period.baseAmountCents)}.
        </p>
      )}
    </Slip>
  );
}

/* One thing waiting. */
function Slip({ hue, children }: { hue: string; children: React.ReactNode }) {
  return (
    <div className="slip" style={{ ["--slip-hue" as string]: hue }}>
      {children}
    </div>
  );
}

function SlipHead({
  who,
  what,
  meta,
  note,
  amount,
}: {
  who: string;
  what?: string;
  meta?: string;
  note?: string;
  amount?: number;
}) {
  return (
    <>
      <span className="slip-what">
        <span style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 600 }}>
          {who}
        </span>
        {what && <span style={{ color: "var(--ink-2)" }}> &middot; {what}</span>}
        {meta && (
          <span className="money" style={{ display: "block", fontSize: 12.5, color: "var(--ink-3)" }}>
            {meta}
          </span>
        )}
        {note && (
          <span style={{ display: "block", fontSize: 13, color: "var(--ink-2)" }}>
            &ldquo;{note}&rdquo;
          </span>
        )}
      </span>
      {amount !== undefined && (
        <span className="money" style={{ fontSize: 20, fontWeight: 700 }}>
          {formatCents(amount)}
        </span>
      )}
    </>
  );
}

/* The tally.
 *
 * One graphite stroke per waiting thing, gated in fives -- the mark you make on
 * the pad by the kettle, and the only handmade thing on a page of printed
 * paper. It reads as MAGNITUDE at a glance; the headline underneath carries the
 * exact count, so neither is doing the other's job.
 *
 * Capped at four gates. Past twenty the shape of a tally stops telling you
 * anything a number wouldn't, and the strokes would wrap.
 */
const TALLY_CAP = 20;

function Tally({ count }: { count: number }) {
  const strokes = Math.min(count, TALLY_CAP);
  const gates = Math.ceil(strokes / 5);

  return (
    <svg
      className="tally"
      aria-hidden="true"
      width={gates * 44}
      height={36}
      viewBox={`0 0 ${gates * 44} 36`}
      style={{ display: "block", overflow: "visible" }}
    >
      {Array.from({ length: strokes }, (_, i) => {
        const gate = Math.floor(i / 5);
        const at = i % 5;
        const x0 = gate * 44;
        const delay = `${i * 45}ms`;

        // The fifth mark strikes through the four before it.
        if (at === 4) {
          return (
            <line
              key={i}
              x1={x0 - 4 + wob(i)}
              y1={31 + wob(i + 7)}
              x2={x0 + 28 + wob(i + 13)}
              y2={5 + wob(i + 21)}
              style={{ animationDelay: delay }}
            />
          );
        }
        const x = x0 + at * 8;
        return (
          <line
            key={i}
            x1={x + wob(i)}
            y1={4 + wob(i + 7)}
            x2={x + wob(i + 13) * 1.6}
            y2={32 + wob(i + 21)}
            style={{ animationDelay: delay }}
          />
        );
      })}
    </svg>
  );
}

/* Deterministic jitter, so the same count always draws the same tally. A random
   one would redraw itself on every poll and look like a twitch. */
function wob(seed: number): number {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return (x - Math.floor(x)) * 2.4 - 1.2;
}

function shortDay(iso: string): string {
  // Split by hand: new Date("2026-08-26") is parsed as UTC midnight, which
  // shows the previous day west of Greenwich.
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

/* Change your own password.
 *
 * Asks for the current one because this app lives on a tablet on a kitchen
 * counter: without it, anyone who found an unlocked parent session could lock
 * the parent out of their own ledger. The server checks it too -- this form
 * only decides what to show.
 */
function ChangePassword({
  onDone,
  onCancel,
}: {
  onDone: () => void;
  onCancel: () => void;
}) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const tooShort = next.length > 0 && next.length < 8;
  const mismatch = confirm.length > 0 && confirm !== next;
  const ready = current.length > 0 && next.length >= 8 && confirm === next;

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
          await api.post("/auth/password", {
            currentPassword: current,
            newPassword: next,
          });
          onDone();
        } catch (err) {
          setError(err instanceof ApiError ? err.message : "That didn't work.");
        } finally {
          setBusy(false);
        }
      }}
      style={{ display: "grid", gap: 12 }}
    >
      <Field label="Current password">
        <input type="password" value={current} autoFocus autoComplete="current-password"
               onChange={(e) => setCurrent(e.target.value)} required />
      </Field>
      <Field label="New password" hint="At least 8 characters.">
        <input type="password" value={next} autoComplete="new-password"
               onChange={(e) => setNext(e.target.value)} required />
      </Field>
      <Field label="New password again">
        <input type="password" value={confirm} autoComplete="new-password"
               onChange={(e) => setConfirm(e.target.value)} required />
      </Field>

      {/* Said before submitting rather than after. The server enforces the
          length; this just stops a pointless round trip. */}
      {tooShort && (
        <p style={{ margin: 0, color: "var(--bad)", fontWeight: 600, fontSize: 14 }}>
          That&rsquo;s too short. Use at least 8 characters.
        </p>
      )}
      {mismatch && (
        <p style={{ margin: 0, color: "var(--bad)", fontWeight: 600, fontSize: 14 }}>
          Those two don&rsquo;t match.
        </p>
      )}
      {error && (
        <p role="alert" style={{ margin: 0, color: "var(--bad)", fontWeight: 600 }}>
          {error}
        </p>
      )}

      <div style={{ display: "flex", gap: 10 }}>
        <button type="submit" disabled={busy || !ready} style={{ flex: 1 }}>
          {busy ? "Saving…" : "Save new password"}
        </button>
        <button type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/* Set or change the address a "forgot password" link goes to.
 *
 * Guarded by the current password for the same reason ChangePassword is --
 * this field decides who can take over the account by email, which makes it
 * worth more than the one-off actions a locked tablet already undoes.
 */
function EmailSettings({
  initialEmail,
  onDone,
  onCancel,
}: {
  initialEmail: string | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [email, setEmail] = useState(initialEmail ?? "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const ready = email.trim().length > 3 && currentPassword.length > 0;

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
          await api.post("/auth/email", { email, currentPassword });
          onDone();
        } catch (err) {
          setError(err instanceof ApiError ? err.message : "That didn't work.");
        } finally {
          setBusy(false);
        }
      }}
      style={{ display: "grid", gap: 12 }}
    >
      <Field label="Email">
        <input type="email" value={email} autoFocus
               onChange={(e) => setEmail(e.target.value)} required />
      </Field>
      <Field label="Current password">
        <input type="password" value={currentPassword} autoComplete="current-password"
               onChange={(e) => setCurrentPassword(e.target.value)} required />
      </Field>

      {error && (
        <p role="alert" style={{ margin: 0, color: "var(--bad)", fontWeight: 600 }}>
          {error}
        </p>
      )}

      <div style={{ display: "flex", gap: 10 }}>
        <button type="submit" disabled={busy || !ready} style={{ flex: 1 }}>
          {busy ? "Saving…" : "Save email"}
        </button>
        <button type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}
